/** Event requests: someone asks for an event to be set up, and each help desk with something to do gets a work order. */
import { upload } from './api';
import type { Floorplan } from './floorplan';
import type { HelpdeskService, Page, WorkOrderStatus } from './types';

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Suggestions for the event type; people can type their own. */
export const EVENT_TYPES = ['Assembly', 'Athletics', 'Ceremony', 'Concert or performance', 'Fundraiser', 'Guest speaker', 'Meeting', 'Parent event'];

export const TEAM_LABEL: Record<HelpdeskService, string> = { IT: 'Technology', FACILITIES: 'Facilities' };

/** Types a browser may show in a tab; anything else downloads. */
export const INLINE_ATTACHMENT_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export interface EventPlace {
  id: string;
  name: string;
  rooms: { id: string; name: string }[];
}

export interface EventRequestOptions {
  techNeeds: string[];
  /** Null when the school doesn't have Facilities. */
  facilitiesNeeds: string[] | null;
  /** Campus buildings and rooms open today. */
  places: EventPlace[];
}

/** A work order an event request opened. */
export interface EventTicket {
  id: string;
  number: number;
  service: HelpdeskService;
  status: WorkOrderStatus;
  title: string;
  assignedTo: { id: string; name: string } | null;
}

export interface EventRequestSummary {
  id: string;
  title: string;
  eventType: string;
  attendees: number;
  startsAt: string;
  setupAt: string;
  breakdownAt: string;
  techNeeds: string[];
  techNeededBy: string | null;
  facilitiesNeeds: string[];
  facilitiesNeededBy: string | null;
  requesterName: string;
  createdById: string | null;
  createdAt: string;
  building: { id: string; name: string } | null;
  room: { id: string; name: string } | null;
  workOrders: EventTicket[];
}

export interface EventRequest extends EventRequestSummary {
  description: string | null;
  techNotes: string | null;
  facilitiesNotes: string | null;
  requesterEmail: string | null;
  attachment: { id: string; fileName: string; contentType: string; sizeBytes: number } | null;
  floorplan: Floorplan | null;
  floorplanUpdatedAt: string | null;
  floorplanUpdatedByName: string | null;
  permissions: {
    /** The requester and both teams can change the floorplan. */
    editFloorplan: boolean;
  };
}

export interface EventRequestPage extends Page<EventRequestSummary> {
  /** Staff on either help desk can list everyone's requests. */
  seesAll: boolean;
}

/** "Gym, Main Hall" */
export const eventPlaceLabel = (event: Pick<EventRequestSummary, 'building' | 'room'>) =>
  [event.room?.name, event.building?.name].filter(Boolean).join(', ') || 'No location';

/** A datetime-local value, in the browser's time, as an ISO time; null when it's empty. */
export const localInputToIso = (value: string) => (value ? new Date(value).toISOString() : null);

/** Uploads the file first, so the request can name it when it's sent. */
export function uploadEventAttachment(file: File) {
  return upload<{ id: string; fileName: string; sizeBytes: number }>('/event-requests/attachments', file, {
    'x-file': encodeURIComponent(JSON.stringify({ fileName: file.name, contentType: file.type || undefined })),
  });
}
